import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import AsyncSubmitButton from '../../ui/AsyncSubmitButton';

const EARNING_FIELDS = [
  ['basic_salary', 'Basic Salary'],
  ['hra', 'HRA'],
  ['da', 'DA'],
  ['special_allowance', 'Special Allowance'],
  ['travel_allowance', 'Travel Allowance'],
  ['medical_allowance', 'Medical Allowance'],
  ['performance_allowance', 'Performance Allowance'],
  ['bonus', 'Bonus'],
  ['other_earnings', 'Other Earnings'],
];

const DEDUCTION_FIELDS = [
  ['pf', 'PF'],
  ['esi', 'ESI'],
  ['professional_tax', 'Professional Tax'],
  ['income_tax', 'Income Tax'],
  ['other_deductions', 'Other Deductions'],
];

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

export default function CrmSalaryStructureForm({ employee, structure, readOnly, onSave }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    const s = structure || {};
    const next = {};
    [...EARNING_FIELDS, ...DEDUCTION_FIELDS].forEach(([k]) => {
      next[k] = s[k] ?? 0;
    });
    next.department = employee?.department || '';
    next.employment_type = employee?.employment_type || 'full_time';
    next.salary_status = employee?.salary_status || 'active';
    next.payroll_cycle = employee?.payroll_cycle || 'joining_date';
    next.effective_from = s.effective_from || employee?.joining_date || '';
    setForm(next);
  }, [structure, employee]);

  const totals = useMemo(() => {
    const gross = EARNING_FIELDS.reduce((sum, [k]) => sum + num(form[k]), 0);
    const ded = DEDUCTION_FIELDS.reduce((sum, [k]) => sum + num(form[k]), 0);
    return { gross, ded, net: Math.max(0, gross - ded) };
  }, [form]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {employee?.first_name} {employee?.last_name}
          <span className="text-muted-foreground font-normal text-sm ml-2">
            {employee?.employee_code} · {employee?.designation || '—'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label>Department</Label>
            <Input value={form.department || ''} disabled={readOnly} onChange={(e) => set('department', e.target.value)} />
          </div>
          <div>
            <Label>Employment Type</Label>
            <Select disabled={readOnly} value={form.employment_type || 'full_time'} onValueChange={(v) => set('employment_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full_time">Full Time</SelectItem>
                <SelectItem value="part_time">Part Time</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="intern">Intern</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Salary Status</Label>
            <Select disabled={readOnly} value={form.salary_status || 'active'} onValueChange={(v) => set('salary_status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Payroll Cycle</Label>
            <Select disabled={readOnly} value={form.payroll_cycle || 'joining_date'} onValueChange={(v) => set('payroll_cycle', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="joining_date">Joining Date</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="bi_weekly">Bi-weekly</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <h3 className="font-semibold mb-2">Earnings</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {EARNING_FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input type="number" step="0.01" disabled={readOnly} value={form[k] ?? 0} onChange={(e) => set(k, e.target.value)} />
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-semibold mb-2">Deductions</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {DEDUCTION_FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input type="number" step="0.01" disabled={readOnly} value={form[k] ?? 0} onChange={(e) => set(k, e.target.value)} />
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 rounded-md border p-3 bg-muted/30">
          <div><div className="text-xs text-muted-foreground">Gross</div><div className="text-lg font-semibold">₹{totals.gross.toLocaleString('en-IN')}</div></div>
          <div><div className="text-xs text-muted-foreground">Deductions</div><div className="text-lg font-semibold">₹{totals.ded.toLocaleString('en-IN')}</div></div>
          <div><div className="text-xs text-muted-foreground">Net</div><div className="text-lg font-semibold">₹{totals.net.toLocaleString('en-IN')}</div></div>
        </div>

        {!readOnly && (
          <AsyncSubmitButton
            onAction={async ({ beginLoading }) => {
              beginLoading();
              await onSave(form);
            }}
          >
            Save Structure
          </AsyncSubmitButton>
        )}
      </CardContent>
    </Card>
  );
}
